import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/ui/components';

type BydaPointDraft = {
  longitude: string;
  latitude: string;
};

type BydaMapCaptureProps = {
  points: BydaPointDraft[];
  addressQuery: string;
  autoLocateKey?: string | number | null;
  disabled?: boolean;
  onChange: (points: BydaPointDraft[]) => void;
};

type LonLat = {
  longitude: number;
  latitude: number;
};

type WorldPoint = {
  x: number;
  y: number;
};

const TILE_SIZE = 256;
const MIN_ZOOM = 13;
const MAX_ZOOM = 19;
const DEFAULT_VIEWPORT = {
  width: 720,
  height: 420,
};
const DEFAULT_CENTER: LonLat = {
  longitude: 144.9631,
  latitude: -37.8136,
};

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function clampLatitude(value: number) {
  return clamp(value, -85.05112878, 85.05112878);
}

function normalizeLongitude(value: number) {
  let next = value;
  while (next < -180) next += 360;
  while (next > 180) next -= 360;
  return next;
}

function getScale(zoom: number) {
  return TILE_SIZE * (2 ** zoom);
}

function lonLatToWorld(point: LonLat, zoom: number): WorldPoint {
  const scale = getScale(zoom);
  const normalizedLongitude = normalizeLongitude(point.longitude);
  const clampedLatitude = clampLatitude(point.latitude);
  const sinLatitude = Math.sin((clampedLatitude * Math.PI) / 180);
  return {
    x: ((normalizedLongitude + 180) / 360) * scale,
    y: (0.5 - (Math.log((1 + sinLatitude) / (1 - sinLatitude)) / (4 * Math.PI))) * scale,
  };
}

function worldToLonLat(point: WorldPoint, zoom: number): LonLat {
  const scale = getScale(zoom);
  const longitude = normalizeLongitude((point.x / scale) * 360 - 180);
  const mercator = Math.PI - ((2 * Math.PI * point.y) / scale);
  const latitude = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(mercator) - Math.exp(-mercator)));
  return {
    longitude,
    latitude: clampLatitude(latitude),
  };
}

function formatCoordinate(value: number) {
  return value.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
}

function toDraftPoint(point: LonLat): BydaPointDraft {
  return {
    longitude: formatCoordinate(point.longitude),
    latitude: formatCoordinate(point.latitude),
  };
}

function parseValidPoints(points: BydaPointDraft[]): LonLat[] {
  return (points || [])
    .map((point) => {
      const rawLongitude = String(point.longitude ?? '').trim();
      const rawLatitude = String(point.latitude ?? '').trim();
      if (!rawLongitude || !rawLatitude) {
        return null;
      }

      return {
        longitude: Number(rawLongitude),
        latitude: Number(rawLatitude),
      };
    })
    .filter((point): point is { longitude: number; latitude: number } => Boolean(point))
    .filter((point) => Number.isFinite(point.longitude) && Number.isFinite(point.latitude))
    .map((point) => ({
      longitude: normalizeLongitude(point.longitude),
      latitude: clampLatitude(point.latitude),
    }));
}

function getCentroid(points: LonLat[]): LonLat | null {
  if (!points.length) return null;
  const total = points.reduce((acc, point) => ({
    longitude: acc.longitude + point.longitude,
    latitude: acc.latitude + point.latitude,
  }), { longitude: 0, latitude: 0 });
  return {
    longitude: total.longitude / points.length,
    latitude: total.latitude / points.length,
  };
}

function getViewportSize(node: HTMLDivElement | null) {
  if (!node) {
    return DEFAULT_VIEWPORT;
  }

  const rect = node.getBoundingClientRect();
  return {
    width: rect.width > 0 ? rect.width : DEFAULT_VIEWPORT.width,
    height: rect.height > 0 ? rect.height : DEFAULT_VIEWPORT.height,
  };
}

function buildPolygonPath(points: Array<{ x: number; y: number }>) {
  if (points.length < 3) return null;
  return `${points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`).join(' ')} Z`;
}

function normalizeAddressQuery(value: string) {
  return String(value || '').trim();
}

export default function BydaMapCapture(props: BydaMapCaptureProps) {
  const parsedPoints = useMemo(() => parseValidPoints(props.points), [props.points]);
  const initialCenter = getCentroid(parsedPoints) || DEFAULT_CENTER;
  const [center, setCenter] = useState<LonLat>(initialCenter);
  const [zoom, setZoom] = useState(parsedPoints.length ? 18 : 16);
  const [viewport, setViewport] = useState(DEFAULT_VIEWPORT);
  const [locating, setLocating] = useState(false);
  const [locationFeedback, setLocationFeedback] = useState<string | null>(null);
  const [tileLoadFailed, setTileLoadFailed] = useState(false);
  const mapRef = useRef<HTMLDivElement | null>(null);
  const suppressAutoCenterRef = useRef(false);
  const lastAutoLocateKeyRef = useRef<string | number | null>(null);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    startWorld: WorldPoint;
    moved: boolean;
    panning: boolean;
  } | null>(null);

  useEffect(() => {
    if (suppressAutoCenterRef.current) {
      suppressAutoCenterRef.current = false;
      return;
    }

    const nextCenter = getCentroid(parsedPoints);
    if (!nextCenter) return;
    setCenter(nextCenter);
  }, [parsedPoints]);

  useEffect(() => {
    const node = mapRef.current;
    const updateViewport = () => {
      setViewport(getViewportSize(node));
    };

    updateViewport();

    if (typeof ResizeObserver === 'undefined' || !node) {
      window.addEventListener('resize', updateViewport);
      return () => {
        window.removeEventListener('resize', updateViewport);
      };
    }

    const observer = new ResizeObserver(() => updateViewport());
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, []);

  const centerWorld = useMemo(() => lonLatToWorld(center, zoom), [center, zoom]);
  const viewportLeft = centerWorld.x - viewport.width / 2;
  const viewportTop = centerWorld.y - viewport.height / 2;
  const mapTiles = useMemo(() => {
    const maxTileIndex = 2 ** zoom;
    const tiles: Array<{ key: string; src: string; left: number; top: number }> = [];
    const startTileX = Math.floor(viewportLeft / TILE_SIZE);
    const endTileX = Math.floor((viewportLeft + viewport.width) / TILE_SIZE);
    const startTileY = Math.floor(viewportTop / TILE_SIZE);
    const endTileY = Math.floor((viewportTop + viewport.height) / TILE_SIZE);

    for (let tileY = startTileY; tileY <= endTileY; tileY += 1) {
      if (tileY < 0 || tileY >= maxTileIndex) continue;
      for (let tileX = startTileX; tileX <= endTileX; tileX += 1) {
        const wrappedTileX = ((tileX % maxTileIndex) + maxTileIndex) % maxTileIndex;
        tiles.push({
          key: `${zoom}-${wrappedTileX}-${tileY}`,
          src: `https://tile.openstreetmap.org/${zoom}/${wrappedTileX}/${tileY}.png`,
          left: tileX * TILE_SIZE - viewportLeft,
          top: tileY * TILE_SIZE - viewportTop,
        });
      }
    }

    return tiles;
  }, [viewport.height, viewport.width, viewportLeft, viewportTop, zoom]);

  const screenPoints = parsedPoints.map((point) => {
    const worldPoint = lonLatToWorld(point, zoom);
    return {
      x: worldPoint.x - viewportLeft,
      y: worldPoint.y - viewportTop,
      longitude: point.longitude,
      latitude: point.latitude,
    };
  });
  const polygonPath = buildPolygonPath(screenPoints);

  const updatePoints = (points: LonLat[]) => {
    suppressAutoCenterRef.current = true;
    props.onChange(points.map(toDraftPoint));
  };

  const addPoint = (point: LonLat) => {
    updatePoints([...parsedPoints, point]);
  };

  const handleMapClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (props.disabled) return;
    if (dragRef.current?.moved) {
      dragRef.current = null;
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const worldPoint = {
      x: viewportLeft + (event.clientX - rect.left),
      y: viewportTop + (event.clientY - rect.top),
    };
    addPoint(worldToLonLat(worldPoint, zoom));
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (props.disabled) return;
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startWorld: centerWorld,
      moved: false,
      panning: event.shiftKey,
    };
    if (event.shiftKey && typeof event.currentTarget.setPointerCapture === 'function') {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current || dragRef.current.pointerId !== event.pointerId) return;
    if (!dragRef.current.panning) return;

    const deltaX = event.clientX - dragRef.current.startX;
    const deltaY = event.clientY - dragRef.current.startY;
    if (Math.abs(deltaX) > 4 || Math.abs(deltaY) > 4) {
      dragRef.current.moved = true;
    }

    if (!dragRef.current.moved) return;

    const nextWorld = {
      x: dragRef.current.startWorld.x - deltaX,
      y: dragRef.current.startWorld.y - deltaY,
    };
    setCenter(worldToLonLat(nextWorld, zoom));
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current || dragRef.current.pointerId !== event.pointerId) return;
    try {
      if (dragRef.current.panning && typeof event.currentTarget.releasePointerCapture === 'function') {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    } catch {}
  };

  const handleDropAtCenter = () => {
    if (props.disabled) return;
    addPoint(center);
  };

  const handleUndoPoint = () => {
    if (props.disabled || parsedPoints.length === 0) return;
    updatePoints(parsedPoints.slice(0, -1));
  };

  const handleClearShape = () => {
    if (props.disabled || parsedPoints.length === 0) return;
    updatePoints([]);
  };

  const handleCreateSiteBox = () => {
    if (props.disabled) return;
    const halfWidth = Math.min(120, viewport.width * 0.18);
    const halfHeight = Math.min(90, viewport.height * 0.15);
    const corners = [
      { x: viewport.width / 2 - halfWidth, y: viewport.height / 2 - halfHeight },
      { x: viewport.width / 2 + halfWidth, y: viewport.height / 2 - halfHeight },
      { x: viewport.width / 2 + halfWidth, y: viewport.height / 2 + halfHeight },
      { x: viewport.width / 2 - halfWidth, y: viewport.height / 2 + halfHeight },
    ].map((point) => worldToLonLat({
      x: viewportLeft + point.x,
      y: viewportTop + point.y,
    }, zoom));

    updatePoints(corners);
  };

  const handleZoom = (delta: number) => {
    setZoom((current) => clamp(current + delta, MIN_ZOOM, MAX_ZOOM));
  };

  const handleLocateAddress = useCallback(async () => {
    if (props.disabled || locating) return;

    const addressQuery = normalizeAddressQuery(props.addressQuery);
    if (!addressQuery) {
      setLocationFeedback('Project address is missing, so the map cannot locate the site yet.');
      return;
    }

    setLocating(true);
    setLocationFeedback(null);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&countrycodes=au&q=${encodeURIComponent(addressQuery)}`,
        {
          method: 'GET',
          referrerPolicy: 'origin',
          headers: {
            Accept: 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Map locate failed (${response.status}).`);
      }

      const payload = await response.json();
      const match = Array.isArray(payload) ? payload[0] : null;
      const longitude = Number(match?.lon);
      const latitude = Number(match?.lat);
      if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
        throw new Error('No mappable result was returned for this address.');
      }

      setCenter({
        longitude: normalizeLongitude(longitude),
        latitude: clampLatitude(latitude),
      });
      setZoom((current) => Math.max(current, 18));
      setLocationFeedback(`Map centred on ${match?.display_name || addressQuery}.`);
    } catch (error) {
      setLocationFeedback(error instanceof Error ? error.message : 'Map locate failed.');
    } finally {
      setLocating(false);
    }
  }, [locating, props.addressQuery, props.disabled]);

  useEffect(() => {
    const key = props.autoLocateKey ?? null;
    if (key === null || key === '' || props.disabled) return;
    if (lastAutoLocateKeyRef.current === key) return;

    lastAutoLocateKeyRef.current = key;
    void handleLocateAddress();
  }, [handleLocateAddress, props.autoLocateKey, props.disabled]);

  return (
    <div
      style={{
        border: '1px solid var(--color-border)',
        borderRadius: '20px',
        overflow: 'hidden',
        background: 'linear-gradient(180deg, rgba(23, 59, 132, 0.06) 0%, rgba(255,255,255,0.98) 100%)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
          alignItems: 'center',
          padding: '16px 18px',
          borderBottom: '1px solid rgba(23, 59, 132, 0.12)',
        }}
      >
        <div style={{ display: 'grid', gap: '4px' }}>
          <strong>Map capture</strong>
          <div className="dgf-inline-help">
            Hold Shift and drag to pan, zoom in for lot-level accuracy, click the map to add polygon corners, or use the quick site-box helper.
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Button variant="secondary" onClick={handleLocateAddress} disabled={props.disabled || locating}>
            {locating ? 'Locating...' : 'Locate Project Address'}
          </Button>
          <Button variant="secondary" onClick={handleCreateSiteBox} disabled={props.disabled}>
            Create Site Box
          </Button>
          <Button variant="secondary" onClick={handleDropAtCenter} disabled={props.disabled}>
            Drop Point At Center
          </Button>
          <Button variant="secondary" onClick={handleUndoPoint} disabled={props.disabled || parsedPoints.length === 0}>
            Undo Point
          </Button>
          <Button variant="secondary" onClick={handleClearShape} disabled={props.disabled || parsedPoints.length === 0}>
            Clear Shape
          </Button>
        </div>
      </div>

      <div
        ref={mapRef}
        aria-label="BYDA map capture"
        onClick={handleMapClick}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{
          position: 'relative',
          height: '420px',
          overflow: 'hidden',
          touchAction: 'none',
          cursor: props.disabled ? 'not-allowed' : 'crosshair',
          background: '#dbeafe',
          userSelect: 'none',
        }}
      >
        {mapTiles.map((tile) => (
          <img
            key={tile.key}
            src={tile.src}
            alt=""
            draggable={false}
            referrerPolicy="origin"
            onError={() => setTileLoadFailed(true)}
            style={{
              position: 'absolute',
              left: `${tile.left}px`,
              top: `${tile.top}px`,
              width: `${TILE_SIZE}px`,
              height: `${TILE_SIZE}px`,
              pointerEvents: 'none',
            }}
          />
        ))}

        <svg
          viewBox={`0 0 ${viewport.width} ${viewport.height}`}
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
          }}
        >
          <defs>
            <pattern id="byda-grid" width="64" height="64" patternUnits="userSpaceOnUse">
              <path d="M 64 0 L 0 0 0 64" fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="1" />
            </pattern>
          </defs>
          <rect x="0" y="0" width={viewport.width} height={viewport.height} fill="url(#byda-grid)" />
          {polygonPath ? (
            <path
              d={polygonPath}
              fill="rgba(35, 123, 75, 0.22)"
              stroke="#237b4b"
              strokeWidth="3"
              strokeLinejoin="round"
            />
          ) : null}
          {screenPoints.map((point, index) => (
            <g key={`map-point-${index}`}>
              <circle cx={point.x} cy={point.y} r="6" fill="#173b84" stroke="rgba(255,255,255,0.95)" strokeWidth="2" />
              <text x={point.x + 10} y={point.y - 10} fontSize="13" fontWeight="700" fill="#173b84">
                {index + 1}
              </text>
            </g>
          ))}
        </svg>

        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: '28px',
            height: '28px',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
          }}
        >
          <div style={{ position: 'absolute', inset: '13px 0 auto 0', height: '2px', background: 'rgba(17, 24, 39, 0.8)' }} />
          <div style={{ position: 'absolute', inset: '0 auto 0 13px', width: '2px', background: 'rgba(17, 24, 39, 0.8)' }} />
          <div style={{ position: 'absolute', inset: '10px', border: '2px solid rgba(17, 24, 39, 0.8)', borderRadius: '999px', background: 'rgba(255,255,255,0.2)' }} />
        </div>

        <div
          style={{
            position: 'absolute',
            right: '14px',
            top: '14px',
            display: 'grid',
            gap: '8px',
          }}
        >
          <button
            type="button"
            aria-label="Zoom in"
            onClick={(event) => {
              event.stopPropagation();
              handleZoom(1);
            }}
            disabled={zoom >= MAX_ZOOM}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              border: '1px solid rgba(23, 59, 132, 0.2)',
              background: 'rgba(255,255,255,0.92)',
              fontSize: '22px',
              cursor: 'pointer',
            }}
          >
            +
          </button>
          <button
            type="button"
            aria-label="Zoom out"
            onClick={(event) => {
              event.stopPropagation();
              handleZoom(-1);
            }}
            disabled={zoom <= MIN_ZOOM}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              border: '1px solid rgba(23, 59, 132, 0.2)',
              background: 'rgba(255,255,255,0.92)',
              fontSize: '22px',
              cursor: 'pointer',
            }}
          >
            -
          </button>
        </div>

        <div
          style={{
            position: 'absolute',
            left: '14px',
            bottom: '14px',
            display: 'grid',
            gap: '4px',
            padding: '10px 12px',
            borderRadius: '14px',
            background: 'rgba(255,255,255,0.92)',
            border: '1px solid rgba(23, 59, 132, 0.1)',
            maxWidth: 'min(360px, calc(100% - 28px))',
          }}
        >
          <div style={{ fontWeight: 600, color: 'var(--color-text)' }}>
            {parsedPoints.length >= 3 ? `${parsedPoints.length} map points ready` : `${parsedPoints.length} map point${parsedPoints.length === 1 ? '' : 's'} captured`}
          </div>
          <div className="dgf-inline-help">
            Center: {formatCoordinate(center.longitude)}, {formatCoordinate(center.latitude)} · Zoom {zoom}
          </div>
          <div className="dgf-inline-help">
            Tiles © OpenStreetMap contributors
          </div>
          {tileLoadFailed ? (
            <div className="dgf-inline-help" style={{ color: 'var(--color-danger, #b42318)' }}>
              Basemap tiles are blocked in this runtime. Use `Locate Project Address`, then `Create Site Box` or click points manually. The polygon still works.
            </div>
          ) : null}
        </div>
      </div>

      <div style={{ padding: '16px 18px', display: 'grid', gap: '10px' }}>
        {locationFeedback ? (
          <div className="dgf-inline-help" style={{ color: locationFeedback.toLowerCase().includes('failed') || locationFeedback.toLowerCase().includes('missing') ? 'var(--color-danger, #b42318)' : 'var(--color-text-muted, #5f6b7a)' }}>
            {locationFeedback}
          </div>
        ) : null}

        <div
          style={{
            display: 'grid',
            gap: '10px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          }}
        >
          {parsedPoints.length ? parsedPoints.map((point, index) => (
            <div
              key={`point-chip-${index}`}
              style={{
                border: '1px solid rgba(23, 59, 132, 0.12)',
                borderRadius: '14px',
                padding: '10px 12px',
                background: 'rgba(255,255,255,0.94)',
                display: 'grid',
                gap: '4px',
              }}
            >
              <strong>{`Point ${index + 1}`}</strong>
              <div className="dgf-inline-help">{formatCoordinate(point.longitude)}, {formatCoordinate(point.latitude)}</div>
            </div>
          )) : (
            <div className="dgf-inline-help">
              No polygon points have been captured yet. Locate the address, then click the map or use the helper buttons to build the enquiry area.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
